/**
 * Additional FFE Equipment Guidance - Equipment Register IDs
 * Maps the exact equipment IDs used by the equipment register to RA-verified guidance
 * CRITICAL: These IDs must match exactly what's in /domains/ffe/equipment.js
 */

export const FFE_ADDITIONAL_GUIDANCE = {
  // === EQUIPMENT REGISTER EXACT ID MAPPINGS ===
  // These IDs come from /domains/ffe/equipment.js - NOT from RA guidance
  
  // Extinguishers - using underscore format from equipment register
  'ffe_extinguisher_outboard_le220kw': {
    id: 'ffe_extinguisher_outboard_le220kw',
    name: 'Fire Extinguisher - Outboard ≤220kW',
    equipment_type: 'fire_extinguisher',
    
    placement: {
      requirements: [
        'Readily accessible for immediate use',
        'Protected from weather and corrosion',
        'Located where outboard engine fire can be safely attacked',
        'Positioned to avoid engine heat and vibration damage'
      ]
    },
    
    specifications: {
      type: 'Portable fire extinguisher rated 34B minimum',
      standards: 'BS EN 3 certified marine fire extinguisher (WBC3 Table 16.1.1.1)',
      trigger: 'Outboard engines with aggregate power ≤220kW',
      quantity: 'One 34B rated extinguisher minimum',
      rating: '34B minimum fire rating for Class B (liquid fuel) fires',
      color_requirement: 'Red color per BS EN 3 standard (no repainting to old colors)'
    },
    
    maintenance: {
      mandatory: [
        'Annual inspection and testing by competent person',
        'Monthly visual inspection of pressure gauge and condition',
        'Replace when pressure drops or damage occurs',
        'Service per manufacturer recommendations'
      ],
      recommended: [
        'Check mounting security monthly',
        'Verify accessibility is not blocked',
        'Inspect discharge hose and nozzle',
        'Ensure operating instructions are legible'
      ]
    },
    
    authority: 'WBC3 Table 16.1.1.1'
  },

  'ffe_extinguisher_outboard_gt220kw': {
    id: 'ffe_extinguisher_outboard_gt220kw',
    name: 'Fire Extinguisher - Outboard >220kW',
    equipment_type: 'fire_extinguisher',
    
    placement: {
      requirements: [
        'Multiple extinguishers distributed for effective coverage',
        'Readily accessible for immediate use from different positions',
        'Protected from weather and engine heat',
        'Positioned to allow safe approach during engine fire'
      ]
    },
    
    specifications: {
      type: 'Multiple portable fire extinguishers with total B rating = 0.3P',
      standards: 'BS EN 3 certified marine fire extinguishers (WBC3 Table 16.1.1.1)',
      trigger: 'Outboard engines with aggregate power >220kW',
      calculation: 'Total B capacity = 0.3 × P (where P = aggregate power in kW)',
      example: 'For 300kW: Total B capacity = 0.3 × 300 = 90B minimum',
      distribution: 'May use multiple extinguishers to achieve total B rating'
    },
    
    maintenance: {
      mandatory: [
        'Annual inspection and testing of all extinguishers',
        'Monthly visual inspection of pressure and condition',
        'Maintain total B rating by replacing expired units',
        'Service per manufacturer recommendations'
      ],
      recommended: [
        'Check all mounting security monthly',
        'Verify accessibility from engine operating positions',
        'Inspect all discharge equipment and instructions',
        'Consider distribution for optimal fire attack angles'
      ]
    },
    
    authority: 'WBC3 Table 16.1.1.1'
  },

  'ffe_extinguisher_inboard_lt120kw': {
    id: 'ffe_extinguisher_inboard_lt120kw',
    name: 'Fire Extinguisher - Inboard Diesel <120kW',
    equipment_type: 'fire_extinguisher',
    
    placement: {
      requirements: [
        'Located adjacent to main entrance of each machinery space (WBC3 Table 16.1.1.1)',
        'Readily accessible from outside machinery space',
        'Protected from machinery space heat and contamination',
        'Clearly visible and marked for emergency use'
      ]
    },
    
    specifications: {
      type: 'Multi-purpose fire extinguisher of appropriate size',
      standards: 'BS EN 3 certified multi-purpose extinguisher (WBC3 Table 16.1.1.1)',
      trigger: 'Inboard diesel engines with power <120kW',
      quantity: 'One extinguisher per machinery space entrance',
      multi_purpose: 'Suitable for Class A (solid materials) and Class B (liquid fuels)',
      size_guidance: 'Appropriate size for machinery space volume and fire risk'
    },
    
    maintenance: {
      mandatory: [
        'Annual inspection and testing by competent person',
        'Monthly visual inspection of pressure gauge and condition',
        'Verify location remains adjacent to machinery entrance',
        'Service per manufacturer recommendations'
      ],
      recommended: [
        'Check mounting security and accessibility monthly',
        'Ensure machinery space entrance remains clear',
        'Verify extinguisher type suitable for machinery fires',
        'Test from normal operating positions'
      ]
    },
    
    authority: 'WBC3 Table 16.1.1.1'
  },
  // Fire Blanket (exact ID from equipment register)
  'ffe_fire_blanket_galley': {
    id: 'ffe_fire_blanket_galley',
    name: 'Fire Blanket',
    equipment_type: 'fire_blanket',
    
    placement: {
      requirements: [
        'Located in area with fire risk (cooking/heating with open flame)',
        'Readily accessible during cooking or heating operations',
        'Protected from contamination but immediately deployable',
        'Positioned for safe use during appliance emergencies'
      ]
    },
    
    specifications: {
      type: 'Fire blanket meeting appropriate standard',
      standards: 'BS EN 1869:2019 Fire Blankets (MIN 698)',
      trigger: [
        'Cooking or heating appliances with open flame',
        'Any area identified by CA as increased fire risk'
      ],
      locations: 'Galley or cooking area where fire risk identified',
      alternative: 'May be used instead of 8A/68B extinguisher for open flame protection',
      deployment: 'Must be readily deployable for smothering fires'
    },
    
    maintenance: {
      mandatory: [
        'Annual inspection by competent person',
        'Check blanket material integrity and fire resistance',
        'Verify deployment mechanism operation',
        'Replace if damaged or contaminated'
      ],
      recommended: [
        'Monthly visual inspection for damage or contamination',
        'Check accessibility during normal cooking operations',
        'Verify blanket deployment instructions remain clear',
        'Ensure crew trained on proper deployment technique'
      ]
    },
    
    authority: 'WBC3 16.5, 16.5.2, MIN 698'
  },

  // Control Position Extinguisher (exact ID from equipment register)
  'ffe_extinguisher_control_position': {
    id: 'ffe_extinguisher_control_position',
    name: 'Control Position Fire Protection',
    equipment_type: 'control_position_extinguisher',
    
    placement: {
      requirements: [
        'Located within 2m from main control position (WBC3 16.3.1.3.1)',
        'Readily accessible during normal vessel operations',
        'Protected from damage but immediately available',
        'Positioned for safe access during control position emergencies'
      ]
    },
    
    specifications: {
      type: 'Multi-purpose portable fire extinguisher',
      standards: 'Portable fire extinguisher per WBC3 16.3.1.3.1',
      trigger: 'Universal requirement for all seagoing vessels',
      distance_requirement: 'Within 2 metres from main control position',
      quantity: 'One extinguisher minimum within specified distance',
      universal_requirement: 'Required regardless of other engine protection arrangements',
      multi_purpose: 'Suitable for electrical and general fires at control position'
    },
    
    maintenance: {
      mandatory: [
        'Annual inspection and testing by competent person',
        'Monthly visual inspection of pressure gauge and condition',
        'Verify distance remains within 2m of control position',
        'Service per manufacturer recommendations'
      ],
      recommended: [
        'Check accessibility during normal vessel operations',
        'Ensure crew familiar with control position extinguisher location',
        'Verify extinguisher type suitable for control position equipment',
        'Test access from normal control positions'
      ]
    },
    
    authority: 'WBC3 16.3.1.3.1'
  },

  // Battery Fixed Suppression (exact ID from equipment register)
  'ffe.battery.fixed_suppression': {
    id: 'ffe.battery.fixed_suppression',
    name: 'Fixed Fire Suppression - Battery',
    equipment_type: 'battery_fire_suppression',
    
    placement: {
      requirements: [
        'Installed in battery boxes and battery rooms (WBC3 Annex 1 3.4.1)',
        'Per battery manufacturer installation requirements',
        'Protected from mechanical damage',
        'Accessible for maintenance and inspection'
      ]
    },
    
    specifications: {
      type: 'Fixed fire suppression system for battery spaces',
      standards: 'Per battery manufacturer requirements (WBC3 Annex 1 3.4.1)',
      trigger: 'All battery electric and hybrid propulsion vessels (mandatory)',
      manufacturer_compliance: 'Must accord with battery manufacturer requirements',
      coverage: 'Battery boxes and battery rooms',
      system_types: ['High pressure water mist (recommended)', 'Foam systems', 'Other manufacturer-approved systems'],
      installation: 'Per manufacturer installation requirements and WBC3 Annex 1'
    },
    
    maintenance: {
      mandatory: [
        'Service per manufacturer requirements (WBC3 Annex 1 3.4.1)',
        'Annual inspection by competent service provider',
        'Verify system compatibility with battery type',
        'Test automatic activation systems'
      ],
      recommended: [
        'Monthly visual inspection of system components',
        'Check control panel indications and alarms',
        'Verify manual activation mechanisms',
        'Coordinate with battery system maintenance schedules'
      ]
    },
    
    authority: 'WBC3 Annex 1 Section 3.4.1'
  },

  // Fire Pump (exact ID from equipment register)
  'ffe_fire_pump_required': {
    id: 'ffe_fire_pump_required',
    name: 'Fire Pump',
    equipment_type: 'fire_pump',
    
    placement: {
      requirements: [
        'Power pump: ensures fire main pressure after machinery space loss (WBC3 16.4.2.1)',
        'Hand pump: located outside machinery space (WBC3 16.4.2.1)',
        'Sea and hose connections for water jet delivery (WBC3 16.4.2.4)',
        'Accessible changeover arrangement if using bilge pump (WBC3 16.4.2.5)'
      ]
    },
    
    specifications: {
      type: 'Power driven self-priming fire pump OR hand fire pump',
      standards: 'Fire pump per WBC3 16.4.2.1',
      trigger: 'All vessels except open boats <8m without substantial enclosure',
      options: [
        'Power driven self-priming fire pump (maintains pressure after machinery space loss)',
        'Hand fire pump outside machinery space (where power pump impracticable)'
      ],
      exemption: 'Open boats <8m length without substantial enclosure (WBC3 16.4.2.3)',
      dual_use: 'Bilge pump may serve as fire pump with changeover (WBC3 16.4.2.5)',
      delivery: 'One jet of water to any part of ship through hose and nozzle (WBC3 16.4.2.4)'
    },
    
    maintenance: {
      mandatory: [
        'Annual pump testing and inspection',
        'Verify sea connection integrity and flow',
        'Test changeover arrangement if dual-use pump',
        'Ensure pump accessibility and operation'
      ],
      recommended: [
        'Monthly pump operation test',
        'Check suction strainer condition',
        'Verify pump priming system operation',
        'Test delivery pressure and flow rate'
      ]
    },
    
    authority: 'WBC3 16.4.2.1, 16.4.2.3, 16.4.2.4, 16.4.2.5'
  },

  // Fire Hose (exact ID from equipment register)
  'ffe_fire_hose_fitted': {
    id: 'ffe_fire_hose_fitted',
    name: 'Fire Hose',
    equipment_type: 'fire_hose',
    
    placement: {
      requirements: [
        'Connected to fire pump with sea and hose connections',
        'Adequate length to reach any part of vessel',
        'Protected from damage and weather when stowed',
        'Readily accessible for emergency deployment'
      ]
    },
    
    specifications: {
      type: 'Fire hose of adequate length with 10mm nozzle and spray nozzle',
      standards: 'Fire hose per WBC3 16.4.2.4',
      trigger: 'Required when vessel has fire pump',
      length: 'Adequate length to reach any part of ship',
      nozzles: [
        '10mm nozzle (WBC3 16.4.2.4)',
        'Suitable spray nozzle (WBC3 16.4.2.4)'
      ],
      delivery: 'Capable of delivering one jet of water to any vessel location',
      material: 'Marine-grade fire hose suitable for seawater service'
    },
    
    maintenance: {
      mandatory: [
        'Annual hose pressure testing and inspection',
        'Check nozzle operation and spray patterns',
        'Verify hose length adequacy for vessel coverage',
        'Test hose connections and couplings'
      ],
      recommended: [
        'Monthly visual inspection for damage or deterioration',
        'Check hose stowage and accessibility',
        'Test nozzle adjustment and spray control',
        'Verify hose remains supple and kink-free'
      ]
    },
    
    authority: 'WBC3 16.4.2.4'
  },

  // Open Boat ≤8m Protection (from RA Part 1, Item 10)
  'ffe_extinguisher_openboat_le8m': {
    id: 'ffe_extinguisher_openboat_le8m',
    name: 'Open Boat ≤8m Protection',
    equipment_type: 'fire_extinguisher',
    
    placement: {
      requirements: [
        'Two extinguishers distributed for accessibility',
        'Protected from weather and sea spray',
        'Secured against vessel motion but readily accessible',
        'Positioned for use from different locations on deck'
      ]
    },
    
    specifications: {
      type: 'Minimum two 34B rated portable fire extinguishers',
      standards: 'BS EN 3 certified marine fire extinguishers (WBC3 Table 16.1.1.1 Note d)',
      trigger: 'Open boats <8m length without substantial enclosure and no cooking',
      quantity: 'Minimum two 34B rated extinguishers required',
      distribution: 'Two separate extinguishers for redundancy and accessibility',
      exemptions: 'Not required if cooking appliances present (different requirements apply)'
    },
    
    maintenance: {
      mandatory: [
        'Annual inspection and testing of both extinguishers',
        'Monthly visual inspection of pressure and weather protection',
        'Verify both extinguishers remain accessible',
        'Service per manufacturer recommendations'
      ],
      recommended: [
        'Check mounting security against sea conditions',
        'Ensure weather protection remains effective',
        'Verify crew can access from normal operating positions',
        'Test accessibility during typical sea conditions'
      ]
    },
    
    authority: 'WBC3 Table 16.1.1.1 Note d'
  },

  // Accommodation Fire Extinguishers (from RA Part 1, Items 6-7)
  'ffe_extinguisher_accommodation_sleeping': {
    id: 'ffe_extinguisher_accommodation_sleeping',
    name: 'Fire Extinguisher - Sleeping Quarters',
    equipment_type: 'fire_extinguisher',
    
    placement: {
      requirements: [
        'Located within sleeping accommodation space',
        'Readily accessible for emergency evacuation',
        'Positioned near escape route for immediate access',
        'Protected from damage during normal sleeping activities'
      ]
    },
    
    specifications: {
      type: '5A/34B rated portable fire extinguisher minimum',
      standards: 'BS EN 3 certified multi-purpose extinguisher (WBC3 16.2.1.2)',
      trigger: 'Accommodation with sleeping but no cooking or heating appliances',
      rating: '5A rating for bedding/furnishing fires, 34B for electrical equipment',
      quantity: 'Minimum one extinguisher per sleeping space',
      accessibility: 'Critical for night-time emergency response'
    },
    
    maintenance: {
      mandatory: [
        'Annual inspection and testing by competent person',
        'Monthly visual inspection of pressure gauge and condition',
        'Verify accessibility from sleeping areas',
        'Service per manufacturer recommendations'
      ],
      recommended: [
        'Check mounting security near sleeping areas',
        'Ensure crew familiar with night-time location',
        'Verify no obstruction by sleeping arrangements',
        'Practice emergency access in low light conditions'
      ]
    },
    
    authority: 'WBC3 16.2.1.2'
  },

  'ffe_extinguisher_accommodation_wheelhouse': {
    id: 'ffe_extinguisher_accommodation_wheelhouse',
    name: 'Fire Extinguisher - Wheelhouse',
    equipment_type: 'fire_extinguisher',
    
    placement: {
      requirements: [
        'Located within wheelhouse accommodation space',
        'Readily accessible during normal vessel operations',
        'Positioned for safe access during emergencies',
        'Protected from damage but immediately available'
      ]
    },
    
    specifications: {
      type: '5A/34B rated portable fire extinguisher minimum',
      standards: 'BS EN 3 certified multi-purpose extinguisher (WBC3 16.2.1.2)',
      trigger: 'Wheelhouse (accommodation space - crew working area)',
      rating: '5A rating for solid materials, 34B for electrical equipment',
      quantity: 'One extinguisher required in wheelhouse',
      separate_requirement: 'Separate from galley protection requirements'
    },
    
    maintenance: {
      mandatory: [
        'Annual inspection and testing by competent person',
        'Monthly visual inspection of pressure gauge and condition',
        'Verify accessibility from wheelhouse positions',
        'Service per manufacturer recommendations'
      ],
      recommended: [
        'Check mounting security within wheelhouse',
        'Ensure crew familiar with location during operations',
        'Verify accessibility during normal navigation duties',
        'Test from normal control positions'
      ]
    },
    
    authority: 'WBC3 16.2.1.2'
  },

  // Cooking Fire Protection (from RA Part 1, Items 8-9)
  'ffe_extinguisher_cooking_noflame': {
    id: 'ffe_extinguisher_cooking_noflame',
    name: 'Fire Protection - No Open Flame',
    equipment_type: 'fire_extinguisher',
    
    placement: {
      requirements: [
        'Located near cooking or heating appliance',
        'Readily accessible during cooking/heating operations',
        'Protected from heat and cooking vapors',
        'Positioned for safe use during appliance fire'
      ]
    },
    
    specifications: {
      type: '5A/34B rated portable fire extinguisher OR fixed system',
      standards: 'BS EN 3 extinguisher or fixed system per WBC3 16.4',
      trigger: 'Cooking or heating appliances without open flame (electric/induction)',
      options: [
        '5A/34B rated portable fire extinguisher',
        'Fixed fire extinguishing system per WBC3 16.4'
      ],
      rating: '5A for solid materials, 34B for electrical fires',
      application: 'Electric or induction cooking/heating equipment'
    },
    
    maintenance: {
      mandatory: [
        'Annual inspection and testing by competent person',
        'Monthly visual inspection if portable extinguisher',
        'Annual service if fixed system per manufacturer',
        'Verify continued suitability for appliance type'
      ],
      recommended: [
        'Check accessibility during normal cooking operations',
        'Ensure crew trained on electrical fire suppression',
        'Verify extinguisher type suitable for electrical equipment',
        'Test from normal cooking positions'
      ]
    },
    
    authority: 'WBC3 Table 16.2.3.1, 16.4'
  },

  'ffe_extinguisher_cooking_flame': {
    id: 'ffe_extinguisher_cooking_flame',
    name: 'Fire Extinguisher - Open Flame Base Requirement',
    equipment_type: 'fire_extinguisher',
    
    placement: {
      requirements: [
        'Located near open flame cooking/heating appliance',
        'Both extinguisher and blanket readily accessible',
        'Protected from heat but immediately available',
        'Positioned for safe use during flame emergencies'
      ]
    },
    
    specifications: {
      type: 'One 5A/34B extinguisher (base requirement for open flame)',
      standards: 'BS EN 3 certified multi-purpose extinguisher (WBC3 16.3.1)',
      trigger: 'Base requirement for cooking or heating with open flame',
      rating: '5A/34B minimum rating required',
      base_protection: 'Always required when open flame cooking/heating present',
      additional_protection: 'Additional protection (blanket/8A68B/fixed) also required'
    },
    
    maintenance: {
      mandatory: [
        'Annual inspection and testing by competent person',
        'Monthly visual inspection of pressure gauge and condition',
        'Verify accessibility during open flame operations',
        'Service per manufacturer recommendations'
      ],
      recommended: [
        'Check accessibility during cooking operations',
        'Ensure crew trained on open flame fire suppression',
        'Verify positioning for safe use during emergencies',
        'Practice emergency shutdown of appliances'
      ]
    },
    
    authority: 'WBC3 16.3.1'
  },

  'ffe_extinguisher_8a68b_galley': {
    id: 'ffe_extinguisher_8a68b_galley',
    name: 'Fire Extinguisher - 8A/68B (Galley)',
    equipment_type: 'fire_extinguisher',
    
    placement: {
      requirements: [
        'Located near open flame galley appliances',
        'Readily accessible during cooking operations',
        'Protected from heat and cooking vapors',
        'Positioned for enhanced galley fire protection'
      ]
    },
    
    specifications: {
      type: '8A/68B rated portable fire extinguisher',
      standards: 'BS EN 3 certified extinguisher with enhanced rating (WBC3 16.3.1)',
      trigger: 'Additional protection for open flame cooking (user selected)',
      rating: '8A/68B enhanced rating for open flame galley protection',
      alternative: 'Alternative to fire blanket for enhanced open flame protection',
      additional_requirement: 'In addition to base 5A/34B requirement'
    },
    
    maintenance: {
      mandatory: [
        'Annual inspection and testing by competent person',
        'Monthly visual inspection of pressure gauge and condition',
        'Verify enhanced rating maintained',
        'Service per manufacturer recommendations'
      ],
      recommended: [
        'Check accessibility during galley operations',
        'Ensure crew trained on enhanced extinguisher use',
        'Verify positioning optimizes galley fire coverage',
        'Test from normal cooking positions'
      ]
    },
    
    authority: 'WBC3 16.3.1'
  },

  // Fixed Fire Systems (from RA Part 1, Item 5)
  'ffe_fixed_system_engine': {
    id: 'ffe_fixed_system_engine',
    name: 'Fixed Fire Extinguishing System - Engine Space',
    equipment_type: 'fixed_fire_system',
    
    placement: {
      requirements: [
        'Installed in machinery space per WBC3 16.4',
        'Administration approved type appropriate to space (WBC3 16.4.1.1)',
        'Protected from mechanical damage',
        'Accessible for maintenance and inspection'
      ]
    },
    
    specifications: {
      type: 'Fixed fire extinguishing system for machinery space',
      standards: 'Administration approved per WBC3 16.4.1.1',
      trigger: 'Required for engines below deck; optional for engines above deck',
      compliance: [
        'Merchant Shipping (Fire Protection – Small Ships) Regulations 1998 (SI 1998 No. 1011)',
        'MSN 1666 installation requirements'
      ],
      system_types: ['CO2 flooding', 'Dry powder', 'Foam systems', 'Water mist'],
      installation: 'Per manufacturer installation requirements (WBC3 16.4.1.2)'
    },
    
    maintenance: {
      mandatory: [
        'Annual service minimum or per manufacturer (WBC3 16.4.1.2)',
        'Meet manufacturer installation and maintenance requirements',
        'Annual inspection by competent service provider',
        'Pressure testing and discharge testing as required'
      ],
      recommended: [
        'Monthly visual inspection of system components',
        'Check control panel indications and alarms',
        'Verify automatic activation systems',
        'Test manual release mechanisms'
      ]
    },
    
    authority: 'WBC3 Table 16.1.1.1, 16.4, MSN 1666, SI 1998 No. 1011'
  },

  'ffe_fixed_system_galley': {
    id: 'ffe_fixed_system_galley',
    name: 'Fixed Fire System (Galley)',
    equipment_type: 'fixed_fire_system',
    
    placement: {
      requirements: [
        'Installed in galley space per WBC3 16.4',
        'Coverage of open flame cooking appliances',
        'Protected from cooking heat and vapors',
        'Accessible for maintenance and inspection'
      ]
    },
    
    specifications: {
      type: 'Fixed fire extinguishing system for galley space',
      standards: 'Fixed system per WBC3 16.4',
      trigger: 'User selected additional protection for open flame cooking',
      alternative: 'Alternative to fire blanket or 8A/68B extinguisher',
      coverage: 'Galley and cooking appliance area',
      installation: 'Per manufacturer installation requirements'
    },
    
    maintenance: {
      mandatory: [
        'Annual service per manufacturer requirements',
        'Annual inspection by competent service provider',
        'Verify system coverage of cooking appliances',
        'Test automatic and manual activation systems'
      ],
      recommended: [
        'Monthly visual inspection of system components',
        'Check system integration with cooking appliance shutdown',
        'Verify manual activation accessibility during cooking',
        'Coordinate with cooking appliance maintenance'
      ]
    },
    
    authority: 'WBC3 16.4'
  },

  'ffe_fixed_system_accommodation': {
    id: 'ffe_fixed_system_accommodation',
    name: 'Fixed Fire System (Accommodation)',
    equipment_type: 'fixed_fire_system',
    
    placement: {
      requirements: [
        'Installed in accommodation spaces per WBC3 16.4',
        'Coverage of all accommodation areas',
        'Protected from damage but accessible',
        'Integrated with accommodation fire detection'
      ]
    },
    
    specifications: {
      type: 'Fixed fire extinguishing system for accommodation spaces',
      standards: 'Fixed system per WBC3 16.4',
      trigger: 'Optional enhancement for accommodation protection',
      mandatory: false,
      coverage: 'Accommodation spaces',
      enhancement: 'Additional protection beyond portable extinguishers'
    },
    
    maintenance: {
      mandatory: [
        'Annual service per manufacturer requirements',
        'Annual inspection by competent service provider',
        'Verify coverage of all accommodation areas',
        'Test system operation and alarms'
      ],
      recommended: [
        'Monthly visual inspection of system components',
        'Check integration with accommodation detection',
        'Verify manual activation accessibility',
        'Coordinate with accommodation equipment maintenance'
      ]
    },
    
    authority: 'WBC3 16.4'
  },

  // Fire Buckets (from RA Part 2, Item 13)
  'ffe_fire_buckets_fitted': {
    id: 'ffe_fire_buckets_fitted',
    name: 'Fire Buckets',
    equipment_type: 'fire_buckets',
    
    placement: {
      requirements: [
        'Readily accessible for emergency use',
        'Protected from weather but immediately available',
        'Distributed for coverage of vessel areas',
        'Secured against vessel motion with lanyards'
      ]
    },
    
    specifications: {
      type: 'Two buckets with lanyards minimum',
      standards: 'Fire buckets per WBC3 16.6.1',
      trigger: 'Where practicable or deemed necessary by Certifying Authority',
      quantity: 'Minimum two buckets with lanyards',
      materials: 'Metal, plastic or canvas suitable for marine service',
      exemptions: [
        'Not required where multiple extinguishers present',
        'Not required where fixed fire system installed'
      ],
      discretion: 'Certifying Authority determines necessity based on vessel configuration'
    },
    
    maintenance: {
      mandatory: [
        'Annual inspection of bucket condition and integrity',
        'Check lanyard attachment and condition',
        'Verify bucket accessibility and security',
        'Replace damaged or deteriorated buckets'
      ],
      recommended: [
        'Monthly check bucket stowage and accessibility',
        'Verify lanyards prevent loss overboard',
        'Check buckets free from contamination',
        'Ensure crew familiar with bucket locations'
      ]
    },
    
    authority: 'WBC3 16.6.1, Note 65'
  },

  // Fire Control Plan and Escape Routes (from RA Part 3, Items 28-29)
  'ffe_plan_fire_control': {
    id: 'ffe_plan_fire_control',
    name: 'Fire Control and Safety Plan',
    equipment_type: 'fire_control_plan',
    
    placement: {
      requirements: [
        'Prominently displayed at main control position (WBC3 15.8.1)',
        'Readily accessible during emergency situations',
        'Protected from weather but clearly visible',
        'Located where emergency response is coordinated'
      ]
    },
    
    specifications: {
      type: 'Fire control and safety plan showing equipment locations',
      standards: 'Fire control plan per WBC3 15.8.1',
      trigger: 'Universal requirement for all seagoing vessels',
      display_requirement: 'Prominently displayed at main control position',
      content: [
        'Fire equipment locations and types',
        'Fire fighting system arrangements',
        'Escape routes and assembly points',
        'Emergency communication arrangements'
      ],
      accessibility: 'Available for emergency response coordination'
    },
    
    maintenance: {
      mandatory: [
        'Annual review and update if vessel configuration changes',
        'Verify plan remains accurate for current equipment installation',
        'Check display condition and legibility',
        'Update after any fire equipment modifications'
      ],
      recommended: [
        'Monthly check plan display condition and visibility',
        'Ensure crew familiar with fire plan contents',
        'Verify plan accuracy during equipment inspections',
        'Review during emergency drills and training'
      ]
    },
    
    authority: 'WBC3 15.8.1'
  },

  'ffe_escape_route_marking': {
    id: 'ffe_escape_route_marking',
    name: 'Escape Route Marking',
    equipment_type: 'escape_route_marking',
    
    placement: {
      requirements: [
        'Clearly marked on both sides of escape route (WBC3 15.7.4)',
        'Visible in normal and emergency lighting conditions',
        'Located at decision points along escape routes',
        'Marked for clear identification of escape purpose'
      ]
    },
    
    specifications: {
      type: 'Escape route marking and signage',
      standards: 'Escape route marking per WBC3 15.7.4',
      trigger: 'Vessels with identified escape routes',
      marking_requirement: 'Both sides of escape route must be marked',
      visibility: 'Clear marking for escape route purpose identification',
      coverage: 'All designated means of escape',
      durability: 'Markings must withstand marine environment'
    },
    
    maintenance: {
      mandatory: [
        'Annual inspection of all escape route markings',
        'Verify markings remain clear and legible',
        'Check both sides of escape routes are properly marked',
        'Replace damaged or faded markings'
      ],
      recommended: [
        'Monthly visual inspection of escape route marking condition',
        'Verify markings visible in different lighting conditions',
        'Check marking visibility is not obstructed',
        'Review marking effectiveness during emergency drills'
      ]
    },
    
    authority: 'WBC3 15.7.4'
  },

  // === DETECTION SYSTEMS (From RA Part 2, Items 15-20) ===

  // Fire Detection - Machinery Space (from RA Part 2, Item 15)
  'ffe_detector_machinery': {
    id: 'ffe_detector_machinery',
    name: 'Fire Detection - Machinery Space',
    equipment_type: 'fire_detector',
    
    placement: {
      requirements: [
        'Fitted in machinery space(s) (WBC3 15.6.2.1)',
        'Positioned per EN 54 standard requirements',
        'Audible from space concerned, accommodation, and control position (WBC3 15.6.2.3)',
        'Capable of being heard with doors closed and machinery running'
      ]
    },
    
    specifications: {
      type: 'Fire detectors detecting smoke and heat, OR flame detectors',
      standards: 'EN 54 compliant fire detectors (WBC3 15.6.2.1)',
      trigger: 'All vessels with machinery space(s)',
      detection_options: [
        'Smoke and heat detectors (combined requirement)',
        'Flame detectors (alternative to smoke/heat)'
      ],
      alarm_requirements: [
        'Audible from space concerned (WBC3 15.6.2.3)',
        'Audible from accommodation spaces (WBC3 15.6.2.3)',
        'Audible from control position(s) (WBC3 15.6.2.3)',
        'Audible with doors closed and machinery running'
      ]
    },
    
    maintenance: {
      mandatory: [
        'Maintained and tested per manufacturer recommendations (WBC3 15.6.1.1)',
        'Annual inspection and testing by competent person',
        'Verify alarm audibility in all required locations',
        'Test detection response to appropriate hazards'
      ],
      recommended: [
        'Monthly functional testing of detection and alarms',
        'Check detector cleanliness and condition',
        'Verify alarm continues to be audible in operational conditions',
        'Test from different machinery space locations'
      ]
    },
    
    authority: 'WBC3 15.6.2.1, 15.6.2.4, 15.6.2.3, EN 54'
  },

  // Fire Detector - Accommodation (from RA Part 2, Item 16)
  'ffe_detector_accommodation': {
    id: 'ffe_detector_accommodation',
    name: 'Fire Detector - Accommodation',
    equipment_type: 'fire_detector',
    
    placement: {
      requirements: [
        'Fitted in accommodation spaces (WBC3 15.6.2.1)',
        'Appropriate type for accommodation hazards (WBC3 15.6.2.2)',
        'Audible from accommodation, control position, and concerned spaces',
        'Positioned per EN 54 installation requirements'
      ]
    },
    
    specifications: {
      type: 'Fire detectors appropriate for accommodation space hazards',
      standards: 'EN 54 compliant fire detectors (WBC3 15.6.2.1)',
      trigger: 'All vessels with accommodation spaces',
      detector_selection: 'Appropriate type to respond to potential hazards (smoke, flame)',
      coverage: 'All accommodation spaces require detection',
      alarm_requirements: [
        'Audible from accommodation spaces (WBC3 15.6.2.3)',
        'Audible from control position(s) (WBC3 15.6.2.3)',
        'Audible from space concerned (WBC3 15.6.2.3)'
      ]
    },
    
    maintenance: {
      mandatory: [
        'Maintained and tested per manufacturer recommendations (WBC3 15.6.1.1)',
        'Annual inspection and testing by competent person',
        'Verify alarm audibility in all accommodation areas',
        'Test detection response appropriate for accommodation hazards'
      ],
      recommended: [
        'Monthly functional testing of detectors and alarms',
        'Check detector positioning remains optimal for accommodation layout',
        'Verify alarms wake sleeping occupants',
        'Test alarm audibility with doors closed'
      ]
    },
    
    authority: 'WBC3 15.6.2.1, 15.6.2.2, 15.6.2.3, EN 54'
  },

  // Fire Detector - Open Flame Spaces (from RA Part 2, Item 17)
  'ffe_detector_openflame_spaces': {
    id: 'ffe_detector_openflame_spaces',
    name: 'Fire Detector - Open Flame Spaces',
    equipment_type: 'fire_detector',
    
    placement: {
      requirements: [
        'Fitted in spaces with open flame, gas, or liquid fuel appliances (WBC3 15.6.2.1)',
        'Appropriate type for open flame and fuel hazards (WBC3 15.6.2.2)',
        'Positioned to detect fires from appliances and fuel systems',
        'Audible from all required locations per WBC3 15.6.2.3'
      ]
    },
    
    specifications: {
      type: 'Fire detectors appropriate for open flame and fuel hazards',
      standards: 'EN 54 compliant fire detectors (WBC3 15.6.2.1)',
      trigger: 'Spaces containing open flame, gas, or liquid fuel appliances',
      appliance_types: [
        'Open flame cooking or heating appliances',
        'Gas appliances (LPG, natural gas)',
        'Liquid fuel appliances (alcohol, diesel heating)'
      ],
      detector_selection: 'Type appropriate for flame, gas, and fuel fire hazards',
      alarm_coverage: 'Audible from space, accommodation, and control positions'
    },
    
    maintenance: {
      mandatory: [
        'Maintained and tested per manufacturer recommendations (WBC3 15.6.1.1)',
        'Annual inspection considering appliance fire risks',
        'Verify detection appropriate for specific appliance hazards',
        'Test alarm audibility in all operational conditions'
      ],
      recommended: [
        'Monthly functional testing during appliance operations',
        'Check detector positioning relative to appliances',
        'Verify detection covers all appliance operating areas',
        'Test alarm response during normal appliance use'
      ]
    },
    
    authority: 'WBC3 15.6.2.1, 15.6.2.2, 15.6.2.3, EN 54'
  },

  // Fire Detector - Electrical Areas (from RA Part 2, Item 18)
  'ffe_detector_electrical_concentration': {
    id: 'ffe_detector_electrical_concentration',
    name: 'Fire Detector - Electrical Areas',
    equipment_type: 'fire_detector',
    
    placement: {
      requirements: [
        'Fitted in areas of concentrated electrical equipment (WBC3 15.6.2.1)',
        'Appropriate type for electrical fire hazards (WBC3 15.6.2.2)',
        'Positioned to detect fires from electrical equipment and wiring',
        'Audible from electrical areas, accommodation, and control positions'
      ]
    },
    
    specifications: {
      type: 'Fire detectors appropriate for electrical equipment hazards',
      standards: 'EN 54 compliant fire detectors (WBC3 15.6.2.1)',
      trigger: 'Areas with concentrated electrical equipment',
      electrical_areas: [
        'Main electrical switchboards',
        'Distribution panels and control rooms',
        'Battery compartments with electrical systems',
        'Areas with concentrated wiring or electrical installations'
      ],
      detector_selection: 'Type appropriate for electrical fires (smoke detection typically)',
      alarm_requirements: 'Audible per WBC3 15.6.2.3 requirements'
    },
    
    maintenance: {
      mandatory: [
        'Maintained and tested per manufacturer recommendations (WBC3 15.6.1.1)',
        'Annual inspection considering electrical fire risks',
        'Verify detection appropriate for electrical equipment present',
        'Coordinate with electrical system maintenance schedules'
      ],
      recommended: [
        'Monthly functional testing in electrical areas',
        'Check detector positioning covers all electrical concentrations',
        'Verify detection sensitivity appropriate for electrical fires',
        'Coordinate testing with electrical equipment operations'
      ]
    },
    
    authority: 'WBC3 15.6.2.1, 15.6.2.2, 15.6.2.3, EN 54'
  },

  // CO Detector - Accommodation (from RA Part 2, Item 19)
  'ffe_detector_co_accommodation': {
    id: 'ffe_detector_co_accommodation',
    name: 'CO Detector - Accommodation',
    equipment_type: 'co_detector',
    
    placement: {
      requirements: [
        'Installed per manufacturer instructions (WBC3 15.6.3.1)',
        'Located in accommodation areas near open flame/gas/liquid fuel appliances',
        'Positioned to detect CO accumulation in accommodation spaces',
        'Consider CO source locations and air circulation patterns'
      ]
    },
    
    specifications: {
      type: 'Carbon monoxide detector(s) for accommodation areas',
      standards: 'CO detector per manufacturer instructions (WBC3 15.6.3.1)',
      trigger: 'Open flame, gas, or liquid fuel appliances in/adjacent to accommodation',
      appliance_sources: [
        'Open flame cooking or heating in/near accommodation',
        'Gas appliances adjacent to accommodation areas',
        'Liquid fuel appliances near accommodation spaces'
      ],
      exemption: 'Not required for electric-only appliances',
      installation: 'Per manufacturer instructions for optimal CO detection'
    },
    
    maintenance: {
      mandatory: [
        'Maintained and tested per manufacturer recommendations (WBC3 15.6.1.1)',
        'Annual inspection and testing by competent person',
        'Battery replacement per manufacturer schedule',
        'Verify continued operation and alarm function'
      ],
      recommended: [
        'Monthly functional test using CO test gas or button',
        'Check battery level indicators regularly',
        'Verify detector positioning remains optimal',
        'Clean detector sensing elements per manufacturer instructions'
      ]
    },
    
    authority: 'WBC3 15.6.3.1, 15.6.1.1'
  },

  // CO Detector - Exhaust Risk Areas (from RA Part 2, Item 20)
  'ffe_detector_co_exhaust': {
    id: 'ffe_detector_co_exhaust',
    name: 'CO Detector - Exhaust Risk Areas',
    equipment_type: 'co_detector',
    
    placement: {
      requirements: [
        'Installed in spaces where exhaust gases may accumulate (WBC3 15.6.3.3)',
        'Positioned to detect CO from potential exhaust leaks',
        'Consider exhaust system routing and potential leak points',
        'Located where exhaust gases could accumulate due to ventilation patterns'
      ]
    },
    
    specifications: {
      type: 'Carbon monoxide detector(s) for exhaust leak protection',
      standards: 'CO detector per WBC3 15.6.3.3',
      trigger: 'Spaces with exhaust gas accumulation risk from leaks',
      risk_areas: [
        'Accommodation spaces adjacent to exhaust systems',
        'Enclosed spaces near exhaust piping',
        'Areas with poor ventilation where exhaust could accumulate',
        'Spaces below exhaust outlets where gases could collect'
      ],
      purpose: 'Detect CO accumulation from exhaust system leaks',
      location_assessment: 'Consider exhaust routing and ventilation characteristics'
    },
    
    maintenance: {
      mandatory: [
        'Maintained and tested per manufacturer recommendations (WBC3 15.6.1.1)',
        'Annual inspection considering exhaust system layout',
        'Verify detector positioning covers potential accumulation areas',
        'Coordinate with exhaust system maintenance and inspection'
      ],
      recommended: [
        'Monthly functional testing in exhaust risk areas',
        'Check exhaust system integrity during detector testing',
        'Verify ventilation effectiveness in protected spaces',
        'Review detector placement if exhaust system modifications made'
      ]
    },
    
    authority: 'WBC3 15.6.3.3, 15.6.1.1'
  },

  // === BATTERY FIRE PROTECTION (From RA Part 3, Items 21-26) ===

  // Battery Fire Detection (from RA Part 3, Item 21)
  'ffe_detector_battery_fire': {
    id: 'ffe_detector_battery_fire',
    name: 'Battery Fire Detection',
    equipment_type: 'battery_fire_detection',
    
    placement: {
      requirements: [
        'Fitted in battery rooms, battery boxes, or spaces containing battery boxes (WBC3 Annex 1 3.2.1)',
        'Positioned to detect fires from battery cells and connections',
        'Auditory and visual alarms required (WBC3 Annex 1 3.2.1)',
        'Coverage of all battery installation areas'
      ]
    },
    
    specifications: {
      type: 'Smoke, heat, and flame detectors (all three types required)',
      standards: 'Suitable detectors per WBC3 Annex 1 Section 3.2.1',
      trigger: 'Battery electric or hybrid propulsion vessels',
      detection_types: [
        'Smoke detectors (required)',
        'Heat detectors (required)', 
        'Flame detectors (required)'
      ],
      alarm_requirements: [
        'Auditory alarms (WBC3 Annex 1 3.2.1)',
        'Visual alarms (WBC3 Annex 1 3.2.1)'
      ],
      coverage: 'Battery rooms, battery boxes, and spaces containing battery boxes'
    },
    
    maintenance: {
      mandatory: [
        'Annual inspection and testing by competent person',
        'Verify all three detection types (smoke, heat, flame) operational',
        'Test auditory and visual alarm functions',
        'Maintain per manufacturer recommendations'
      ],
      recommended: [
        'Monthly functional testing of detection systems',
        'Check detector positioning covers all battery areas',
        'Verify alarm audibility and visibility during battery operations',
        'Coordinate testing with battery system maintenance'
      ]
    },
    
    authority: 'WBC3 Annex 1 Section 3.2.1'
  },

  // Battery Gas Detection (from RA Part 3, Item 22)
  'ffe_detector_battery_gas': {
    id: 'ffe_detector_battery_gas',
    name: 'Battery Gas Detection',
    equipment_type: 'battery_gas_detection',
    
    placement: {
      requirements: [
        'Fitted where risk assessment determines gas accumulation possible (WBC3 Annex 1 3.2.2)',
        'Located in battery rooms and enclosed battery spaces',
        'Positioned to detect explosive gas accumulation near batteries',
        'Consider battery ventilation patterns and gas collection points'
      ]
    },
    
    specifications: {
      type: 'Gas detectors for explosive gas detection',
      standards: 'Gas detectors per WBC3 Annex 1 Section 3.2.2',
      trigger: 'Risk assessment determines explosive gas accumulation possible up to LEL',
      detection_threshold: '60% LEL automatic disconnect required (WBC3 Annex 1 3.2.2)',
      automatic_action: 'Disconnect battery system at 60% LEL',
      risk_based: 'Required only where risk assessment identifies LEL accumulation risk',
      gas_types: 'Hydrogen and other explosive gases from battery systems'
    },
    
    maintenance: {
      mandatory: [
        'Annual calibration and testing by competent person',
        'Verify 60% LEL automatic disconnect function',
        'Test detector response to target gases',
        'Review risk assessment periodically for continued applicability'
      ],
      recommended: [
        'Monthly functional testing with test gas',
        'Check detector sensitivity and calibration status',
        'Verify automatic disconnect operates correctly',
        'Monitor battery room ventilation effectiveness'
      ]
    },
    
    authority: 'WBC3 Annex 1 Section 3.2.2'
  },

  // Battery Fire Extinguisher (from RA Part 3, Item 24)
  'ffe_extinguisher_battery': {
    id: 'ffe_extinguisher_battery',
    name: 'Battery Fire Extinguisher',
    equipment_type: 'battery_fire_extinguisher',
    
    placement: {
      requirements: [
        'Readily accessible to battery spaces (WBC3 Annex 1 3.5.2)',
        'Protected from weather and contamination',
        'Located for safe approach during battery fire',
        'Positioned near battery room/box entrances'
      ]
    },
    
    specifications: {
      type: 'Portable fire extinguishers suitable for battery fires',
      standards: 'Minimum 34B rating per WBC3 Annex 1 3.5.2',
      trigger: 'Battery electric and hybrid propulsion vessels',
      quantity: 'Minimum two extinguishers with 34B rating',
      additional_requirement: 'In addition to Section 16 fire protection requirements',
      manufacturer_type: 'Per battery manufacturer type requirements (WBC3 Annex 1 3.5.2)',
      rating: 'Minimum 34B fire rating for each extinguisher'
    },
    
    maintenance: {
      mandatory: [
        'Annual inspection and testing by competent person',
        'Monthly visual inspection of pressure gauge and condition',
        'Verify extinguisher type suitable for battery fires',
        'Service per manufacturer recommendations'
      ],
      recommended: [
        'Check mounting security and accessibility monthly',
        'Ensure crew trained on battery fire suppression techniques',
        'Verify extinguisher compatibility with battery type',
        'Test accessibility during normal battery operations'
      ]
    },
    
    authority: 'WBC3 Annex 1 Section 3.5.2'
  },

  // Hydrocarbon Gas Detector (from RA Part 3, Item 25)
  'ffe_detector_gas_hydrocarbon': {
    id: 'ffe_detector_gas_hydrocarbon',
    name: 'Hydrocarbon Gas Detector',
    equipment_type: 'gas_detector',
    
    placement: {
      requirements: [
        'Fitted in spaces with gas appliances or gas leak risk (WBC3 15.6.4.1)',
        'Positioned to detect gas accumulation patterns',
        'Consider gas density and ventilation characteristics',
        'Located where flammable gas may leak or accumulate'
      ]
    },
    
    specifications: {
      type: 'Gas detector and alarm system for flammable gases',
      standards: 'Suitable gas detector per WBC3 15.6.4.1',
      trigger: 'Spaces with gas appliances or flammable gas leak potential',
      detection: 'Hydrocarbon and flammable gas detection',
      alarm_required: 'Alarm system required with detector (WBC3 15.6.4.1)',
      coverage: 'All spaces where gas may leak or accumulate',
      gas_types: ['LPG', 'Natural gas', 'Other flammable hydrocarbon gases']
    },
    
    maintenance: {
      mandatory: [
        'Annual calibration and testing by competent person',
        'Verify alarm system operation with detector',
        'Test detector response to target gases',
        'Maintain per manufacturer recommendations'
      ],
      recommended: [
        'Monthly functional testing with test gas',
        'Check detector positioning relative to gas appliances',
        'Verify alarm audibility in operational conditions',
        'Monitor gas appliance connections for leak prevention'
      ]
    },
    
    authority: 'WBC3 15.6.4.1'
  },

  // Fire Detection System - Li-ion Battery (from RA Part 3, Item 26)
  'ffe_detector_lithium_system': {
    id: 'ffe_detector_lithium_system',
    name: 'Fire Detection System - Li-ion Battery',
    equipment_type: 'lithium_fire_detection',
    
    placement: {
      requirements: [
        'Fitted in lithium battery rooms, boxes, and containing spaces (WBC3 Annex 1 3.2.1)',
        'All three detector types required in each space',
        'Positioned for optimal detection of lithium battery fires',
        'Auditory and visual alarms required'
      ]
    },
    
    specifications: {
      type: 'Complete fire detection system with smoke, heat, and flame detectors',
      standards: 'Suitable detectors per WBC3 Annex 1 Section 3.2.1',
      trigger: 'Vessels with lithium-ion battery installations',
      required_detectors: [
        'Smoke detectors (mandatory)',
        'Heat detectors (mandatory)',
        'Flame detectors (mandatory)'
      ],
      alarm_system: 'Auditory and visual alarms for all three detection types',
      coverage: 'All lithium battery installation areas',
      integration: 'May integrate with battery management system'
    },
    
    maintenance: {
      mandatory: [
        'Annual inspection and testing of all three detector types',
        'Verify smoke, heat, and flame detection functions separately',
        'Test auditory and visual alarm systems',
        'Maintain per manufacturer recommendations for each type'
      ],
      recommended: [
        'Monthly functional testing of complete detection system',
        'Check detector positioning covers all lithium battery areas',
        'Verify alarm integration with battery management system',
        'Test emergency response procedures for different fire types'
      ]
    },
    
    authority: 'WBC3 Annex 1 Section 3.2.1'
  },

  // Additional Fire Pump - Dangerous Goods (from RA Part 3, Item 30)
  'ffe_fire_pump_dangerous_goods': {
    id: 'ffe_fire_pump_dangerous_goods',
    name: 'Additional Fire Pump - Dangerous Goods',
    equipment_type: 'additional_fire_pump',
    
    placement: {
      requirements: [
        'Second powered fire pump in addition to WBC3 16.4.2 requirements',
        'Located for effective fire fighting capability',
        'Protected from dangerous goods fire damage',
        'Accessible for maintenance and operation'
      ]
    },
    
    specifications: {
      type: 'Second powered fire pump for dangerous goods vessels',
      standards: 'Powered fire pump per WBC3 29.8.1',
      trigger: 'Vessels carrying dangerous goods (excluding Light Duty certificates)',
      additional_requirement: 'In addition to standard fire pump requirements',
      light_duty_restriction: 'Dangerous goods not permitted on Light Duty certificates',
      pump_type: 'Powered fire pump (not hand pump)',
      capacity: 'Suitable for dangerous goods fire fighting requirements'
    },
    
    maintenance: {
      mandatory: [
        'Annual pump testing and inspection',
        'Verify pump operation and delivery capacity',
        'Test pump accessibility and emergency operation',
        'Service per manufacturer recommendations'
      ],
      recommended: [
        'Monthly pump operation test',
        'Check pump protection from dangerous goods areas',
        'Verify pump capability for dangerous goods fire scenarios',
        'Coordinate maintenance with primary fire pump schedule'
      ]
    },
    
    authority: 'WBC3 29.8.1'
  },

  // === EQUIPMENT REGISTER SPECIFIC MAPPINGS ===
  // These are the exact names appearing in Equipment Register debug output without guidance
  
  // Portable Fire Extinguisher - 5A/34B (Sleeping Quarters)
  'Portable Fire Extinguisher - 5A/34B (Sleeping Quarters)': {
    id: 'Portable Fire Extinguisher - 5A/34B (Sleeping Quarters)',
    name: 'Portable Fire Extinguisher - 5A/34B (Sleeping Quarters)',
    equipment_type: 'fire_extinguisher',
    
    placement: {
      requirements: [
        'Located in or adjacent to sleeping quarters',
        'Readily accessible in emergency',
        'Protected from damage and weather',
        'Clear escape route positioning'
      ]
    },
    
    specifications: {
      type: 'Multi-purpose dry powder extinguisher rated 5A/34B',
      standards: 'BS EN 3 certified marine extinguisher',
      trigger: 'Sleeping quarters accommodation spaces',
      rating: '5A rating for solid fuel fires, 34B rating for liquid fuel fires'
    },
    
    maintenance: {
      mandatory: [
        'Annual inspection and testing by competent person',
        'Monthly pressure gauge check',
        'Service per manufacturer recommendations',
        'Replace if pressure drops or damage occurs'
      ]
    },
    
    authority: 'WBC3 Section 16.2.1.2'
  },

  // Portable Fire Extinguisher - 8A/68B (Galley)
  'Portable Fire Extinguisher - 8A/68B (Galley)': {
    id: 'Portable Fire Extinguisher - 8A/68B (Galley)',
    name: 'Portable Fire Extinguisher - 8A/68B (Galley)',
    equipment_type: 'fire_extinguisher',
    
    placement: {
      requirements: [
        'Located in or adjacent to galley area',
        'Additional galley protection beyond basic requirement',
        'Accessible from galley exit route',
        'Protected from cooking heat and grease'
      ]
    },
    
    specifications: {
      type: 'Multi-purpose dry powder extinguisher rated 8A/68B',
      standards: 'BS EN 3 certified marine extinguisher',
      trigger: 'Enhanced galley protection where cooking operations present higher risk',
      rating: '8A rating for solid fuel fires, 68B rating for liquid fuel fires'
    },
    
    maintenance: {
      mandatory: [
        'Annual inspection and testing by competent person',
        'Monthly pressure gauge and condition check',
        'Check for grease contamination',
        'Service per manufacturer recommendations'
      ]
    },
    
    authority: 'WBC3 Section 16.2.1.1'
  },

  // Fixed Fire System (Galley)
  'Fixed Fire System (Galley)': {
    id: 'Fixed Fire System (Galley)',
    name: 'Fixed Fire System (Galley)',
    equipment_type: 'fixed_system',
    
    placement: {
      requirements: [
        'Fixed installation covering galley cooking area',
        'Automatic or manual activation from safe location',
        'Complete coverage of cooking surfaces and equipment',
        'Integration with gas supply shutdown'
      ]
    },
    
    specifications: {
      type: 'Fixed fire suppression system (wet chemical or equivalent)',
      standards: 'Marine-approved fixed system for galley application',
      trigger: 'Galley areas with significant cooking fire risk',
      activation: 'Automatic heat detection or manual remote activation'
    },
    
    maintenance: {
      mandatory: [
        'Annual system inspection and testing',
        'Monthly visual inspection of nozzles and piping',
        'Test manual activation controls',
        'Service per manufacturer recommendations'
      ]
    },
    
    authority: 'WBC3 Section 16.2.1.1'
  },

  // Fire Detector - Sleeping Quarters  
  'Fire Detector - Sleeping Quarters': {
    id: 'Fire Detector - Sleeping Quarters',
    name: 'Fire Detector - Sleeping Quarters',
    equipment_type: 'detector',
    
    placement: {
      requirements: [
        'Located in sleeping quarters/cabin areas',
        'Positioned for optimal smoke detection',
        'Clear of air circulation patterns that might prevent detection',
        'Audible alarm capability throughout accommodation'
      ]
    },
    
    specifications: {
      type: 'Smoke detector with audible alarm',
      standards: 'Marine-approved smoke detection system',
      trigger: 'Sleeping quarters with accommodation spaces',
      power: 'Battery backup or vessel electrical system with backup'
    },
    
    maintenance: {
      mandatory: [
        'Monthly alarm test and battery check',
        'Annual cleaning and sensitivity test',
        'Replace batteries per manufacturer schedule',
        'Service per manufacturer recommendations'
      ]
    },
    
    authority: 'WBC3 Section 16.3'
  },

  // Fire Detector - Wheelhouse
  'Fire Detector - Wheelhouse': {
    id: 'Fire Detector - Wheelhouse',
    name: 'Fire Detector - Wheelhouse',
    equipment_type: 'detector',
    
    placement: {
      requirements: [
        'Located in wheelhouse/control position',
        'Positioned to detect fire in control equipment',
        'Integration with general alarm system',
        'Clear of navigation equipment that might interfere'
      ]
    },
    
    specifications: {
      type: 'Smoke detector with visual and audible alarm',
      standards: 'Marine-approved detection system suitable for wheelhouse',
      trigger: 'Wheelhouse areas with electrical equipment and accommodation',
      integration: 'Connected to general alarm system'
    },
    
    maintenance: {
      mandatory: [
        'Monthly alarm test and inspection',
        'Annual sensitivity and coverage test',
        'Check integration with alarm system',
        'Service per manufacturer recommendations'
      ]
    },
    
    authority: 'WBC3 Section 16.3'
  },

  // CO Detector - Accommodation Spaces
  'CO Detector - Accommodation Spaces': {
    id: 'CO Detector - Accommodation Spaces',
    name: 'CO Detector - Accommodation Spaces',
    equipment_type: 'detector',
    
    placement: {
      requirements: [
        'Located in accommodation spaces where CO risk exists',
        'Positioned at breathing level in sleeping areas',
        'Away from direct ventilation flow',
        'Audible alarm throughout accommodation'
      ]
    },
    
    specifications: {
      type: 'Carbon monoxide detector with audible alarm',
      standards: 'Marine-approved CO detection system',
      trigger: 'Accommodation spaces with potential CO exposure from engines or appliances',
      sensitivity: 'Low-level CO detection capability'
    },
    
    maintenance: {
      mandatory: [
        'Monthly alarm test and battery check',
        'Annual calibration and sensor test',
        'Replace sensors per manufacturer schedule',
        'Service per manufacturer recommendations'
      ]
    },
    
    authority: 'WBC3 Section 16.3'
  }
};